<main>
  <loading-overlay [isLoading]="isLoading > 0"></loading-overlay>
  <p-toast position="bottom-right"></p-toast>

  <header>
    <div class="headline">
      <h1>{{ getDocumentTitle() }}</h1>
      @if (currentDocument) {
        <p-tag [value]="getStatusLabel(currentDocument.status)" [severity]="getStatusSeverity(currentDocument.status)" />
        @if (currentDocument.isAdministrative) {
          <p-tag severity="warn" [value]="'Purchase_Order_Administrative' | translateWF: 'Administrative'" />
        }
      }
    </div>
    <div class="buttons">
      <!-- Location Label -->
      <p-tag [value]="selectedLocationLabel" severity="secondary" [icon]="selectedLocationIcon" />

      @if (activeTabIndex === '1' && currentDocument) {
        @if (strategy.supportsReportDownload() && strategy.canDownloadReport(currentDocument.status)) {
          <p-button label="{{ 'Download_Report' | translateWF: 'Download Report' }}" icon="pi pi-download" severity="secondary" [outlined]="true" (click)="downloadReport()" />
        }
        <span class="separator"></span>
        @for (action of availableActions; track action.id) {
          <p-button [label]="action.label" [icon]="action.icon" [severity]="action.severity || 'secondary'" [disabled]="action.disabled" (click)="executeAction(action)" />
        }
      }
    </div>
  </header>

  <div class="content">
    <p-panel>
      <p-card data-variant="compact">
        <p-tabs [(value)]="activeTabIndex">
          <p-tablist>
            <p-tab value="0">{{ 'Supply_Warehouse_Overview' | translateWF: 'Overview' }}</p-tab>
            <p-tab value="1">{{ 'Document_Detail' | translateWF: 'Document Detail' }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <!-- TAB 1: Overview - Muestra PO/Iniciador + GRNs existentes -->
            <p-tabpanel value="0">
              <!-- Panel de información del documento iniciador (PO o DR) -->
              @if (initiatorDocument) {
                <p-panel [header]="strategy.initiatorName + ' Information'" styleClass="mb-4">
                  <p-card data-variant="flat">
                    <div class="grid-12">
                      @if (documentType === DocumentType.GoodsReceiptNote && isPurchaseOrder(initiatorDocument)) {
                        <!-- Purchase Order Info -->
                        <div class="span-4">
                          <strong>{{ 'PO_Number' | translateWF: 'PO Number' }}:</strong>
                          <p>{{ initiatorDocument.purchaseOrderNumber }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'PO_Provider' | translateWF: 'Provider' }}:</strong>
                          <p>{{ initiatorDocument.provider?.name }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'PO_Status' | translateWF: 'Status' }}:</strong>
                          <p>{{ getInitiatorStatusLabel(initiatorDocument.status) }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'PO_Order_Date' | translateWF: 'Order Date' }}:</strong>
                          <p>{{ initiatorDocument.createdDate | date: 'dd/MM/yyyy' }}</p>
                        </div>
                      }
                      @if (documentType === DocumentType.MaterialIssue && isExternalRequisition(initiatorDocument)) {
                        <!-- External Requisition Info -->
                        <div class="span-4">
                          <strong>{{ 'ER_Number' | translateWF: 'ER Number' }}:</strong>
                          <p>{{ initiatorDocument.number }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'ER_Status' | translateWF: 'Status' }}:</strong>
                          <p>{{ localHelperService.getLocalizedValue(initiatorDocument.statusName) }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'ER_Department' | translateWF: 'Department' }}:</strong>
                          <p>{{ initiatorDocument.departmentName || 'N/A' }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'ER_Created_Date' | translateWF: 'Created Date' }}:</strong>
                          <p>{{ initiatorDocument.createdDate | date: 'dd/MM/yyyy HH:mm' }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'ER_Created_By' | translateWF: 'Created By' }}:</strong>
                          <p>{{ initiatorDocument.createdBy }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'ER_Approvers' | translateWF: 'Approvers' }}:</strong>
                          <p>{{ initiatorDocument.requiredApprovers || 0 }}</p>
                        </div>
                      }
                      @if (documentType === DocumentType.ProviderReturn && isGoodsReceiptInitiator(initiatorDocument)) {
                        <!-- Goods Receipt Note Info (PR initiator) -->
                        <div class="span-4">
                          <strong>{{ 'GRN_Number' | translateWF: 'GRN Number' }}:</strong>
                          <p>{{ initiatorDocument.number }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'Common_Status' | translateWF: 'Status' }}:</strong>
                          <p-tag [value]="getInitiatorStatusLabel(initiatorDocument.status)" severity="success" />
                        </div>
                        <div class="span-4">
                          <strong>{{ 'Common_Location' | translateWF: 'Location' }}:</strong>
                          <p>{{ localHelperService.getLocalizedValue(initiatorDocument.locationName) || initiatorDocument.locationCode }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'GRN_Received_Date' | translateWF: 'Received Date' }}:</strong>
                          <p>{{ initiatorDocument.receivedDate | date: 'dd/MM/yyyy' }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'Common_Created_By' | translateWF: 'Created By' }}:</strong>
                          <p>{{ initiatorDocument.createdBy }}</p>
                        </div>
                      }
                      @if (documentType === DocumentType.InternalReturn && isDispatchRequest(initiatorDocument)) {
                        <!-- Dispatch Request Info (IR initiator) -->
                        <div class="span-4">
                          <strong>{{ 'DR_Number' | translateWF: 'DR Number' }}:</strong>
                          <p>{{ initiatorDocument.number }}</p>
                        </div>
                        <div class="span-4">
                          <strong class="mr-2">{{ 'DR_Status' | translateWF: 'Status' }}:</strong>
                          <p-tag [value]="getInitiatorStatusLabel(initiatorDocument.status)" />
                        </div>
                        <div class="span-4">
                          <strong>{{ 'DR_Location' | translateWF: 'Location' }}:</strong>
                          <p>{{ localHelperService.getLocalizedValue(initiatorDocument.locationName) || initiatorDocument.locationCode }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'DR_Dispatched_Date' | translateWF: 'Dispatched Date' }}:</strong>
                          <p>{{ initiatorDocument.createdDate | date: 'dd/MM/yyyy' }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'Common_Created_By' | translateWF: 'Created By' }}:</strong>
                          <p>{{ initiatorDocument.createdBy }}</p>
                        </div>
                      }
                    </div>
                  </p-card>
                </p-panel>
              }

              <!-- Tabla de documentos de transacción existentes (GRNs, MIs, IRs, PRs) -->
              <!-- Solo se muestra para documentos que generan hijos (ej: GRN desde PO, MI desde ER, IR desde DR, PR desde GRN) -->
              @if (
                documentType === DocumentType.GoodsReceiptNote ||
                documentType === DocumentType.MaterialIssue ||
                documentType === DocumentType.InternalReturn ||
                documentType === DocumentType.ProviderReturn
              ) {
                <p-panel [header]="'Existing_Transaction_Documents' | translateWF: 'Existing Transaction Documents'" styleClass="mb-4">
                  <ng-template pTemplate="icons">
                    @if (
                      !(documentType === DocumentType.InternalReturn && allItemsFullyReturned) &&
                      (documentType !== DocumentType.InternalReturn || isInternalReturnsAdmin) &&
                      (documentType !== DocumentType.ProviderReturn || isProviderReturnAdmin) &&
                      (documentType !== DocumentType.GoodsReceiptNote || isAdmin) &&
                      !isInitiatorPOReceived
                    ) {
                      <p-button
                        label="{{ 'Create_New_Document' | translateWF: 'Create New ' + documentType }}"
                        icon="pi pi-plus"
                        severity="success"
                        (click)="createNewDocument()"
                      />
                    }
                  </ng-template>
                  <p-card data-variant="compact">
                    <p-table [value]="transactionDocuments" [paginator]="true" [rows]="10" [rowsPerPageOptions]="[10, 25, 50]">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ 'Document_Number' | translateWF: 'Number' }}</th>
                          <th>{{ 'Document_Status' | translateWF: 'Status' }}</th>
                          <th>{{ 'Document_Created_Date' | translateWF: 'Created' }}</th>
                          <th>{{ 'Document_Location' | translateWF: 'Location' }}</th>
                          @if (documentType === DocumentType.GoodsReceiptNote) {
                            <th>{{ 'GRN_Unit_Price' | translateWF: 'Unit Price' }}</th>
                            <th>{{ 'GRN_Total_Price' | translateWF: 'Total Price' }}</th>
                          }
                          <th>{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                        </tr>
                      </ng-template>

                      <ng-template pTemplate="body" let-doc>
                        <tr>
                          <td>{{ doc.number }}</td>
                          <td>
                            <p-tag [value]="getStatusLabel(doc.status)" [severity]="getStatusSeverity(doc.status)" />
                          </td>
                          <td>{{ doc.createdDate | date: 'dd/MM/yyyy HH:mm' }}</td>
                          <td>{{ localHelperService.getLocalizedValue(doc.locationName) || doc.locationCode }}</td>
                          @if (documentType === DocumentType.GoodsReceiptNote) {
                            <td>{{ getDocumentUnitPrice(doc) !== null ? (getDocumentUnitPrice(doc) | number: '1.2-2') + ' ' + getCurrencyName(currency) : '—' }}</td>
                            <td>{{ getDocumentTotalPrice(doc) | number: '1.2-2' }} {{ getCurrencyName(currency) }}</td>
                          }
                          <td>
                            <p-button
                              icon="pi pi-eye"
                              [rounded]="true"
                              [text]="true"
                              severity="info"
                              (click)="selectDocument(doc.id); activeTabIndex = '1'"
                              pTooltip="{{ 'Common_View_Edit' | translateWF: 'View/Edit' }}"
                            />
                            @if (documentType === DocumentType.GoodsReceiptNote && isProviderReturnAdmin && doc.status === GoodsReceiptStatus.Completed) {
                              <p-button
                                icon="pi pi-reply"
                                [rounded]="true"
                                [text]="true"
                                severity="warn"
                                (click)="navigateToProviderReturn(doc.id)"
                                pTooltip="{{ 'Create_Provider_Return' | translateWF: 'Create Provider Return' }}"
                              />
                            }
                          </td>
                        </tr>
                      </ng-template>

                      <ng-template pTemplate="emptymessage">
                        <tr>
                          <td [attr.colspan]="documentType === DocumentType.GoodsReceiptNote ? 7 : 5" class="text-center">
                            {{ 'No_Documents_Found' | translateWF: 'No documents found. Click "Create New" to start.' }}
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </p-card>
                </p-panel>

                <!-- Tabla de items del documento iniciador (Items del PO o Entries de ER) -->
                @if (getInitiatorItems().length > 0) {
                  <p-panel [header]="strategy.initiatorName + ' Items'">
                    <p-card data-variant="compact">
                      <p-table [value]="getInitiatorItems()" [paginator]="true" [rows]="10">
                        <ng-template pTemplate="header">
                          <tr>
                            @for (col of initiatorItemsColumns; track col.field) {
                              <th>{{ col.headerKey ? (col.headerKey | translateWF: col.header) : col.header }}</th>
                            }
                          </tr>
                        </ng-template>

                        <ng-template pTemplate="body" let-item>
                          <tr>
                            <td>{{ item.itemCode }}</td>
                            @if (isPurchaseOrder(initiatorDocument)) {
                              <td>{{ localHelperService.getLocalizedValue(item.item.itemDescription) }}</td>
                              <td>{{ item.owner?.number }}</td>
                            } @else {
                              <td>{{ localHelperService.getLocalizedValue(item.itemDisplayName) }}</td>
                            }
                            <td>{{ item.quantity | number: '1.2-2' }}</td>
                            @if (
                              isExternalRequisition(initiatorDocument) ||
                              isPurchaseOrder(initiatorDocument) ||
                              isDispatchRequest(initiatorDocument) ||
                              isGoodsReceiptInitiator(initiatorDocument)
                            ) {
                              <td>{{ uoms[item.uom] }}</td>
                            }
                            @if (isPurchaseOrder(initiatorDocument)) {
                              <td>{{ item.unitPrice | number: '1.2-2' }} {{ getCurrencyName(currency) }}</td>
                              <td>{{ item.totalPrice | number: '1.2-2' }} {{ getCurrencyName(currency) }}</td>
                            }
                            @if (!isExternalRequisition(initiatorDocument)) {
                              <td>{{ getActualTransactionQuantity(item.itemCode, item.owner?.reference) | number: '1.2-2' }}</td>
                            }
                            <td>{{ item.quantity - getActualTransactionQuantity(item.itemCode, item.owner?.reference) | number: '1.2-2' }}</td>
                            <td>
                              @if (currentDocument && canEdit()) {
                                <p-button
                                  icon="pi pi-plus"
                                  [rounded]="true"
                                  [text]="true"
                                  severity="success"
                                  pTooltip="{{ 'Add_To_Current_Document' | translateWF: 'Add to current document' }}"
                                />
                              }
                            </td>
                          </tr>
                        </ng-template>
                      </p-table>
                    </p-card>
                  </p-panel>
                }
              }
            </p-tabpanel>

            <!-- TAB 2: Document Detail - Edición/Vista del documento actual -->
            <p-tabpanel value="1">
              @if (currentDocument) {
                <!-- Header del documento actual -->
                <p-panel [header]="'Document_Information' | translateWF: 'Document Information'" styleClass="mb-4">
                  <p-card data-variant="flat">
                    <div class="grid-12">
                      <div class="span-3">
                        <strong>{{ 'Document_Number' | translateWF: 'Number' }}:</strong>
                        <p>{{ currentDocument.number || 'Not assigned yet' }}</p>
                      </div>
                      <div class="span-3">
                        <strong class="mr-2">{{ 'Document_Status' | translateWF: 'Status' }}:</strong>
                        <p-tag [value]="getStatusLabel(currentDocument.status)" [severity]="getStatusSeverity(currentDocument.status)" />
                      </div>
                      <div class="span-3">
                        <strong>{{ 'Document_Location' | translateWF: 'Location' }}:</strong>
                        <p>{{ localHelperService.getLocalizedValue(currentDocument.locationName) || currentDocument.locationCode }}</p>
                      </div>
                      <div class="span-3">
                        <strong>{{ 'Common_Created' | translateWF: 'Created' }}:</strong>
                        <p>{{ currentDocument.createdDate | date: 'dd/MM/yyyy HH:mm' }}</p>
                      </div>
                    </div>

                    <!-- Supply Request Origin Info - Only for Dispatch Request -->
                    @if (documentType === DocumentType.DispatchRequest && isDispatchRequest(currentDocument) && $any(currentDocument).supplyRequestNumber) {
                      <p-divider />
                      <div class="grid-12">
                        <div class="span-4">
                          <strong>{{ 'Supply_Request_Number' | translateWF: 'Supply Request' }}:</strong>
                          <p>{{ $any(currentDocument).supplyRequestNumber }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'Requested_By' | translateWF: 'Requested By' }}:</strong>
                          <p>{{ $any(currentDocument).supplyRequestCreatedBy || '-' }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'Department' | translateWF: 'Department' }}:</strong>
                          <p>{{ localHelperService.getLocalizedValue($any(currentDocument).supplyRequestDepartmentName) || '-' }}</p>
                        </div>
                      </div>
                    }
                  </p-card>
                </p-panel>

                <!-- Return Date Info - Only for Internal Return -->
                @if (documentType === DocumentType.InternalReturn && isInternalReturn(currentDocument)) {
                  <p-panel [header]="'IR_Title' | translateWF: 'Internal Return'" styleClass="mb-4">
                    <p-card data-variant="flat">
                      <div class="grid-12">
                        <div class="span-4">
                          <strong>{{ 'IR_Return_Date' | translateWF: 'Return Date' }}:</strong>
                          <p>{{ currentDocument.returnDate | date: 'dd/MM/yyyy HH:mm' }}</p>
                        </div>
                      </div>
                    </p-card>
                  </p-panel>
                }

                <!-- Provider Return Info - Only for Provider Return -->
                @if (documentType === DocumentType.ProviderReturn && isProviderReturn(currentDocument)) {
                  <p-panel [header]="'PR_Title' | translateWF: 'Provider Return'" styleClass="mb-4">
                    <p-card data-variant="flat">
                      <div class="grid-12">
                        <div class="span-4">
                          <strong>{{ 'PR_Return_Date' | translateWF: 'Return Date' }}:</strong>
                          <p>{{ currentDocument.returnDate | date: 'dd/MM/yyyy HH:mm' }}</p>
                        </div>
                        <div class="span-8">
                          <strong>{{ 'PR_Return_Reason' | translateWF: 'Return Reason' }}:</strong>
                          @if (canEdit()) {
                            <input
                              type="text"
                              pInputText
                              [(ngModel)]="currentDocument.returnReason"
                              placeholder="{{ 'PR_Return_Reason_Placeholder' | translateWF: 'Enter return reason' }}"
                              class="w-full mt-1"
                              (ngModelChange)="onDocumentChange()"
                            />
                          } @else {
                            <p>{{ currentDocument.returnReason || '-' }}</p>
                          }
                        </div>
                      </div>
                    </p-card>
                  </p-panel>
                }

                <!-- Notes Section - Only for Dispatch Request -->
                @if (documentType === DocumentType.DispatchRequest && isDispatchRequest(currentDocument)) {
                  <p-panel [header]="'Document_Notes' | translateWF: 'Notes'" styleClass="mb-4">
                    <p-card data-variant="flat">
                      <csl-standard-notes
                        [userLite]="currentUserLite!"
                        [notes]="$any(currentDocument).notes || []"
                        [viewOnly]="!canEdit()"
                        (noteAdded)="onDispatchNoteAdded($event)"
                      />
                    </p-card>
                  </p-panel>
                }

                <!-- Datos generales del GRN -->
                @if (documentType === DocumentType.GoodsReceiptNote && isPurchaseOrder(initiatorDocument)) {
                  <p-panel [header]="'General_Data' | translateWF: 'General Data'" styleClass="mb-4">
                    <p-card data-variant="flat">
                      <div class="grid-12">
                        <div class="span-4">
                          <strong>{{ 'Provider' | translateWF: 'Provider' }}:</strong>
                          <p>{{ providerName }}</p>
                        </div>
                        <div class="span-4">
                          <strong>{{ 'Invoice_Number' | translateWF: 'Invoice Number' }}:</strong>
                          @if (canEdit()) {
                            <input
                              type="text"
                              pInputText
                              [(ngModel)]="$any(currentDocument).invoiceNumber"
                              placeholder="{{ 'Invoice_Number' | translateWF: 'Invoice Number' }}"
                              class="w-full mt-1"
                            />
                          } @else {
                            <p>{{ $any(currentDocument).invoiceNumber || '-' }}</p>
                          }
                        </div>
                      </div>
                    </p-card>
                  </p-panel>
                }

                <!-- Tabla de líneas/entries del documento -->
                <p-panel [header]="'Document_Lines' | translateWF: 'Lines'">
                  <p-card data-variant="compact">
                    <p-table [value]="getDocumentLines()" [paginator]="true" [rows]="10" dataKey="lineId" [expandedRowKeys]="expandedRowKeys">
                      <ng-template pTemplate="header">
                        <tr>
                          <th style="width: 50px"></th>
                          @for (col of tableColumns; track col.field) {
                            <th [style.width]="col.width">{{ col.headerKey ? (col.headerKey | translateWF: col.header) : col.header }}</th>
                          }
                          @if (documentType === DocumentType.DispatchRequest) {
                            <th>{{ 'Supply_Request_Comment' | translateWF: 'SR Comment' }}</th>
                          }
                          @if (documentType === DocumentType.ProviderReturn) {
                            <th style="width: 200px">{{ 'PR_Line_Return_Reason' | translateWF: 'Line Return Reason' }}</th>
                          }
                          @if (canEdit()) {
                            <th style="width: 80px">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                          }
                        </tr>
                      </ng-template>

                      <ng-template pTemplate="body" let-line let-i="rowIndex" let-expanded="expanded">
                        <tr>
                          <!-- Expand/Collapse toggle -->
                          <td>
                            <p-button
                              type="button"
                              [pRowToggler]="line"
                              [text]="true"
                              [rounded]="true"
                              [plain]="true"
                              [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                            />
                          </td>
                          <td>{{ line.itemCode }}</td>
                          <td>{{ line.itemDisplayName?.en }}</td>
                          <td>
                            @if (canEdit() && strategy.canEditQuantities(currentDocument.status)) {
                              <p-inputNumber
                                [(ngModel)]="line.quantity"
                                (ngModelChange)="onLineQuantityChange(line)"
                                [minFractionDigits]="2"
                                [maxFractionDigits]="2"
                                [min]="0.01"
                                [max]="getMaxReturnQuantityForPRLine(line)"
                                styleClass="w-full"
                              />
                            } @else {
                              {{ line.quantity | number: '1.2-2' }}
                            }
                          </td>
                          <td>{{ uoms[line.uom] }}</td>
                          @if (documentType === DocumentType.GoodsReceiptNote) {
                            <td>{{ line.unitPrice ?? 0 | number: '1.2-2' }} {{ getCurrencyName(currency) }}</td>
                            <td>{{ line.quantity * (line.unitPrice ?? 0) * (1 + (line.taxRate ?? 0)) | number: '1.2-2' }} {{ getCurrencyName(currency) }}</td>
                          }
                          <td>
                            <!-- Summary of locations -->
                            @if (strategy.locationAssignmentType === 'storage') {
                              @if (line.storageLocations && line.storageLocations.length > 0) {
                                <p-tag
                                  [value]="line.storageLocations.length + ' ' + ('Common_Location_Count' | translateWF: 'location(s)')"
                                  severity="success"
                                  icon="pi pi-map-marker"
                                />
                              } @else {
                                <p-tag [value]="'Common_No_Locations' | translateWF: 'No locations'" severity="warn" icon="pi pi-exclamation-triangle" />
                              }
                            } @else {
                              @if (line.sourceLocations && line.sourceLocations.length > 0) {
                                <p-tag [value]="line.sourceLocations.length + ' ' + ('Common_Location_Count' | translateWF: 'location(s)')" severity="success" icon="pi pi-send" />
                              } @else {
                                <p-tag [value]="'Common_No_Locations' | translateWF: 'No locations'" severity="warn" icon="pi pi-exclamation-triangle" />
                              }
                            }
                          </td>
                          <!-- Supply Request Comment - Only for Dispatch Request (readonly) -->
                          @if (documentType === DocumentType.DispatchRequest) {
                            <td>
                              <span class="text-gray-600 italic">{{ line.supplyRequestComment || '-' }}</span>
                            </td>
                          }
                          <!-- Line Return Reason - Only for Provider Return (editable) -->
                          @if (documentType === DocumentType.ProviderReturn) {
                            <td>
                              @if (canEdit()) {
                                <input
                                  type="text"
                                  pInputText
                                  [(ngModel)]="line.lineReturnReason"
                                  placeholder="{{ 'PR_Line_Return_Reason_Placeholder' | translateWF: 'Enter line return reason' }}"
                                  class="w-full"
                                  (ngModelChange)="onDocumentChange()"
                                />
                              } @else {
                                <span>{{ line.lineReturnReason || '-' }}</span>
                              }
                            </td>
                          }
                          @if (documentType !== DocumentType.ProviderReturn) {
                            <td>
                              @if (canEdit()) {
                                <input type="text" pInputText [(ngModel)]="line.comments" placeholder="{{ 'Common_Comments' | translateWF: 'Comments' }}" styleClass="w-full" />
                              } @else {
                                {{ line.comments }}
                              }
                            </td>
                          }
                          <td>
                            @if (canEdit()) {
                              <p-button
                                icon="pi pi-trash"
                                [rounded]="true"
                                [text]="true"
                                severity="danger"
                                pTooltip="{{ 'Common_Remove' | translateWF: 'Remove' }}"
                                (click)="removeLineFromDocument(i)"
                              />
                            }
                          </td>
                        </tr>
                      </ng-template>

                      <!-- Row Expansion Template for Storage Locations -->
                      <ng-template pTemplate="rowexpansion" let-line>
                        <tr>
                          <td [attr.colspan]="tableColumns.length + 1">
                            <div class="p-4">
                              <div class="flex justify-content-between items-center mb-3">
                                <h4 class="m-0 mr-2">
                                  {{
                                    strategy.locationAssignmentType === 'storage'
                                      ? ('GRN_Storage_Locations' | translateWF: 'Storage Locations')
                                      : ('MI_Source_Locations' | translateWF: 'Source Locations')
                                  }}
                                </h4>
                                @if (canEdit()) {
                                  <p-button [label]="'Common_Add_Location' | translateWF: 'Add Location'" icon="pi pi-plus" size="small" (onClick)="addLocationToLine(line)" />
                                }
                              </div>

                              <!-- Storage/Source Locations Table -->
                              @if (strategy.locationAssignmentType === 'storage') {
                                <!-- Incoming: storageLocations -->
                                @if (line.storageLocations && line.storageLocations.length > 0) {
                                  <p-table [value]="line.storageLocations" styleClass="p-datatable-sm">
                                    <ng-template pTemplate="header">
                                      <tr>
                                        <th class="col-location-document">{{ 'Document_Location' | translateWF: 'Location' }}</th>
                                        <th class="col-location-quantity">{{ 'Common_Quantity' | translateWF: 'Quantity' }}</th>
                                        @if (canEdit()) {
                                          <th class="col-location-action">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                                        }
                                      </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-location let-locIndex="rowIndex">
                                      <tr>
                                        <td>
                                          @if (canEdit()) {
                                            <p-treeSelect
                                              [options]="line.locationTreeNodes || []"
                                              [(ngModel)]="location.selectedTreeNode"
                                              [placeholder]="'Common_Select_Location' | translateWF: 'Select Location'"
                                              styleClass="w-full"
                                              [showClear]="true"
                                              selectionMode="single"
                                              appendTo="body"
                                              (ngModelChange)="onLocationSelected(location, $event, line)"
                                              [loading]="line.isLoadingLocations"
                                            >
                                              <ng-template pTemplate="value" let-node>
                                                @if (node) {
                                                  {{ getLocationDisplayPath(node) }}
                                                } @else {
                                                  Select Location
                                                }
                                              </ng-template>
                                            </p-treeSelect>
                                          } @else {
                                            {{ location.locationCode }}
                                          }
                                        </td>
                                        <td>
                                          @if (canEdit()) {
                                            <p-inputNumber
                                              [(ngModel)]="location.quantity"
                                              (ngModelChange)="validateLocationQuantities(line)"
                                              [minFractionDigits]="2"
                                              [maxFractionDigits]="2"
                                              [min]="0"
                                              [max]="line.quantity"
                                              styleClass="w-full"
                                            />
                                          } @else {
                                            {{ location.quantity | number: '1.2-2' }}
                                          }
                                        </td>
                                        @if (canEdit()) {
                                          <td>
                                            <p-button
                                              icon="pi pi-trash"
                                              [rounded]="true"
                                              [text]="true"
                                              severity="danger"
                                              size="small"
                                              (onClick)="removeLocationFromLine(line, locIndex)"
                                              [pTooltip]="'Common_Remove_Location' | translateWF: 'Remove Location'"
                                            />
                                          </td>
                                        }
                                      </tr>
                                    </ng-template>
                                    <ng-template pTemplate="footer">
                                      <tr>
                                        <td>
                                          <strong>{{ 'Common_Total_Allocated' | translateWF: 'Total Allocated' }}:</strong>
                                        </td>
                                        <td>
                                          <strong
                                            [class.text-red-500]="getTotalLocationQuantity(line.storageLocations) !== line.quantity"
                                            [class.text-green-500]="getTotalLocationQuantity(line.storageLocations) === line.quantity"
                                          >
                                            {{ getTotalLocationQuantity(line.storageLocations) | number: '1.2-2' }}
                                            / {{ line.quantity | number: '1.2-2' }}
                                          </strong>
                                        </td>
                                        @if (canEdit()) {
                                          <td></td>
                                        }
                                      </tr>
                                    </ng-template>
                                  </p-table>
                                } @else {
                                  <p class="text-center p-4 text-gray-500">
                                    {{
                                      'Common_No_Storage_Locations'
                                        | translateWF
                                          : 'No storage
                    locations assigned'
                                    }}
                                  </p>
                                }
                              } @else {
                                <!-- Outgoing: sourceLocations -->
                                @if (line.sourceLocations && line.sourceLocations.length > 0) {
                                  <p-table [value]="line.sourceLocations" styleClass="p-datatable-sm">
                                    <ng-template pTemplate="header">
                                      <tr>
                                        <th style="width: 20%">{{ 'Document_Location' | translateWF: 'Location' }}</th>
                                        <th style="width: 60%">{{ 'Common_Quantity' | translateWF: 'Quantity' }}</th>
                                        @if (canEdit()) {
                                          <th style="width: 100px">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                                        }
                                      </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-location let-locIndex="rowIndex">
                                      <tr>
                                        <td>
                                          @if (canEdit()) {
                                            <p-treeSelect
                                              [options]="line.locationTreeNodes || []"
                                              [(ngModel)]="location.selectedTreeNode"
                                              [placeholder]="'Common_Select_Location' | translateWF: 'Select Location'"
                                              styleClass="w-full"
                                              appendTo="body"
                                              [showClear]="true"
                                              selectionMode="single"
                                              (ngModelChange)="onLocationSelected(location, $event, line)"
                                              [loading]="line.isLoadingLocations"
                                            >
                                              <ng-template pTemplate="value" let-node>
                                                @if (node) {
                                                  {{ getLocationDisplayPath(node) }}
                                                } @else {
                                                  Select Location
                                                }
                                              </ng-template>
                                            </p-treeSelect>
                                          } @else {
                                            {{ location.locationCode }}
                                          }
                                        </td>
                                        <td>
                                          @if (canEdit()) {
                                            <p-inputNumber
                                              [(ngModel)]="location.quantity"
                                              (ngModelChange)="validateLocationQuantities(line)"
                                              [minFractionDigits]="2"
                                              [maxFractionDigits]="2"
                                              [min]="0"
                                              [max]="line.quantity"
                                              styleClass="w-full"
                                            />
                                          } @else {
                                            {{ location.quantity | number: '1.2-2' }}
                                          }
                                        </td>
                                        @if (canEdit()) {
                                          <td>
                                            <p-button
                                              icon="pi pi-trash"
                                              [rounded]="true"
                                              [text]="true"
                                              severity="danger"
                                              size="small"
                                              (onClick)="removeLocationFromLine(line, locIndex)"
                                              [pTooltip]="'Common_Remove_Location' | translateWF: 'Remove Location'"
                                            />
                                          </td>
                                        }
                                      </tr>
                                    </ng-template>
                                    <ng-template pTemplate="footer">
                                      <tr>
                                        <td>
                                          <strong>{{ 'Common_Total_Allocated' | translateWF: 'Total Allocated' }}:</strong>
                                        </td>
                                        <td>
                                          <strong
                                            [class.text-red-500]="getTotalLocationQuantity(line.sourceLocations) !== line.quantity"
                                            [class.text-green-500]="getTotalLocationQuantity(line.sourceLocations) === line.quantity"
                                          >
                                            {{ getTotalLocationQuantity(line.sourceLocations) | number: '1.2-2' }}
                                            / {{ line.quantity | number: '1.2-2' }}
                                          </strong>
                                        </td>
                                        @if (canEdit()) {
                                          <td></td>
                                        }
                                      </tr>
                                    </ng-template>
                                  </p-table>
                                } @else {
                                  <p class="text-center p-4 text-gray-500">
                                    {{
                                      'Common_No_Source_Locations'
                                        | translateWF
                                          : 'No source locations
                    assigned'
                                    }}
                                  </p>
                                }
                              }
                            </div>
                          </td>
                        </tr>
                      </ng-template>

                      <ng-template pTemplate="emptymessage">
                        <tr>
                          <td [attr.colspan]="tableColumns.length + 1" class="text-center">
                            {{ 'No_Lines_Found' | translateWF: 'No lines found' }}
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </p-card>
                </p-panel>
              } @else {
                <!-- No document selected -->
                <div class="text-center p-8">
                  <i class="pi pi-inbox" style="font-size: 4rem; color: var(--surface-500)"></i>
                  <p class="mt-4">
                    {{ 'Select_Or_Create_Document' | translateWF: 'Please select or create a document from the Overview tab' }}
                  </p>
                </div>
              }
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      </p-card>
    </p-panel>
  </div>

  <!-- Warehouse Selection Dialog for GRN creation -->
  <p-dialog
    header="{{ 'Select_Destination_Warehouse' | translateWF: 'Select Destination Warehouse' }}"
    [(visible)]="showWarehouseDialog"
    [modal]="true"
    [style]="{ width: '400px' }"
  >
    <div class="col">
      <p class="text-sm text-gray-600">
        {{
          'Warehouse_Selection_Info'
            | translateWF
              : 'This Purchase Order has items for multiple warehouses. Please
        select which warehouse you want to receive items for.'
        }}
      </p>
      <div class="field">
        <label for="warehouseSelect" class="font-semibold">{{ 'Warehouse' | translateWF: 'Warehouse' }}</label>
        <p-select
          id="warehouseSelect"
          [options]="warehouseOptions"
          [(ngModel)]="selectedWarehouse"
          optionLabel="label"
          optionValue="value"
          placeholder="{{ 'Select_Warehouse' | translateWF: 'Select a warehouse' }}"
          styleClass="w-full mt-2"
        />
      </div>
    </div>
    <ng-template pTemplate="footer">
      <p-button label="{{ 'Common_Cancel' | translateWF: 'Cancel' }}" icon="pi pi-times" severity="secondary" [text]="true" (click)="showWarehouseDialog = false" />
      <p-button label="{{ 'Create_GRN' | translateWF: 'Create GRN' }}" icon="pi pi-check" severity="primary" [disabled]="!selectedWarehouse" (click)="onWarehouseSelected()" />
    </ng-template>
  </p-dialog>
</main>
